import { createRoot } from "react-dom/client";
import React, { useEffect, useState } from "react";
import { DatePickerButton } from "../dashboard/forms/date_field";
import * as luxon from "luxon";
import { DateTime } from "luxon";
import chrono from "chrono-node";
import { TIMESTAMP_TYPES } from "../../../common/timestamp_types";

window.onload = () => {
	document.title = "Timestamp Converter - sesh.fyi";
	const container = document.getElementById("timestamp-converter");
	const root = createRoot(container);
	root.render(App());
};

export default function App() {
	return <TimestampConverter />;
}

function TimestampConverter() {
	const [date, setDate] = useState(luxon.DateTime.now());

	const is_markdown_list_visible = date && date.isValid;

	return (
		<>
			<DateInput date={date} setDate={(new_date) => setDate(new_date)} />
			<br />
			<div
				className={`${is_markdown_list_visible ? "" : " is-invisible"}`}
			>
				<MarkdownList key={date?.toISO()} date={date} />
			</div>
		</>
	);
}

function DateInput(props: {
	date: luxon.DateTime;
	setDate: (new_date: luxon.DateTime) => void;
}) {
	const [rawInput, setRawInput] = useState(
		luxon.DateTime.now().toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)
	);

	return (
		<div className="control">
			<div className="columns is-mobile is-vcentered is-centered is-gapless">
				<div className="column is-6-desktop is-8-tablet is-10-mobile">
					<input
						className="input"
						placeholder={"Ex: 08/21/23 at 6pm"}
						type="text"
						value={rawInput}
						onChange={(val) => {
							setRawInput(val.target.value);
							const parsed_date = parseTime(val.target.value);
							props.setDate(parsed_date);
						}}
					/>
				</div>
				<div className="column is-1 has-text-centered">
					<DatePickerButton
						key={props.date?.toISO()}
						initial_date={props.date?.toJSDate()}
						allow_past_days={true}
						onSubmit={(raw_date) => {
							const date = luxon.DateTime.fromJSDate(raw_date);
							setRawInput(
								date.toLocaleString(
									DateTime.DATETIME_MED_WITH_WEEKDAY
								)
							);

							props.setDate(date);
						}}
						modal_subtitle={"Select time using your local timezone"}
					/>
				</div>
			</div>
		</div>
	);
}

function MarkdownList(props: { date: luxon.DateTime }) {
	const [copiedIndex, setCopied] = useState(-1);
	const [copiedCounter, setCopiedCounter] = useState(0);

	const elements: JSX.Element[] = [];

	let index = 0;
	for (const key in TIMESTAMP_TYPES) {
		const formatter = TIMESTAMP_TYPES[key];

		const current_index = index;

		elements.push(
			<MarkdownItemRow
				key={key}
				isCopied={index === copiedIndex}
				setIsCopied={() => {
					setCopied(current_index);
					setCopiedCounter(copiedCounter + 1);
				}}
				date={props.date}
				formatter={formatter}
				suffix={key}
			/>
		);
		index++;
	}

	return (
		<div
			key={copiedCounter.toString()}
			className="columns is-mobile is-centered markdown-list"
		>
			<div className="column is-narrow">
				<table className="self-center">
					<tbody>{elements}</tbody>
				</table>
			</div>
		</div>
	);
}

function MarkdownItemRow(props: {
	date: luxon.DateTime;
	suffix: string;
	isCopied: boolean;
	setIsCopied: () => void;
	formatter: (typeof TIMESTAMP_TYPES)[string];
}) {
	if (!props.date) {
		return <td></td>;
	}

	const markdown = `<t:${props.date.toSeconds().toFixed(0)}:${props.suffix}>`;

	const markdown_id = `${props.suffix}_markdown`;

	useEffect(() => {
		if (props.isCopied) {
			selectText(markdown_id);
		}
	}, [props.isCopied]);

	const dateMarkdown = (
		<span
			id={markdown_id}
			className="command-example--inline discord-timestamp-example"
		>
			{markdown}
		</span>
	);

	const dateString = <>{props.formatter(props.date)}</>;

	const copyIcon = <i className="far fa-copy manual-link"></i>;

	return (
		<>
			<tr
				className="timestamp-row is-hidden-mobile"
				onClick={(event) => {
					navigator.clipboard.writeText(markdown);
					props.setIsCopied();
				}}
			>
				<td>
					{dateMarkdown}
					{props.isCopied && (
						<div className="tooltip-parent">
							<div className="tooltip has-text-centered">
								<span className="has-text-centered">
									copied
								</span>
							</div>
						</div>
					)}
				</td>
				<td className="has-text-weight-bold">{dateString}</td>
				<td className="">{copyIcon}</td>
			</tr>
			<tr
				className="timestamp-row is-hidden-tablet"
				onClick={(event) => {
					navigator.clipboard.writeText(markdown);

					props.setIsCopied();
				}}
			>
				<td>
					<div className="has-text-weight-bold timestamp-name">
						{dateString}
					</div>
					<div className="columns is-mobile is-variable is-1">
						<div className="column is-narrow">{dateMarkdown}</div>
						<div className="column is-narrow">{copyIcon}</div>
						{props.isCopied && (
							<div className="tooltip-parent">
								<div className="tooltip has-text-centered">
									<span className="has-text-centered">
										copied
									</span>
								</div>
							</div>
						)}
					</div>
				</td>
			</tr>
		</>
	);
}

function parseTime(str: string): luxon.DateTime {
	let chrono_results = chrono.parse(str);

	if (chrono_results[0]?.start) {
		return luxon.DateTime.fromJSDate(chrono_results[0].start.date());
	}
}

// https://gist.github.com/gubatron/6599366
function selectText(element_id: string) {
	const doc = document;
	const text = doc.getElementById(element_id);

	//all others
	const selection = window.getSelection();
	const range = doc.createRange();
	range.selectNodeContents(text);
	selection.removeAllRanges();
	selection.addRange(range);
}
